/**
 * The Mystic's room, option B (mystic-page-2026-10-29 OUTLINE "Decided
 * (later)" §1; PNGs 27/28). Tapping a Mystic lands in the reading-session
 * look: the large portrait, the round ✕ back where the seeker came from, the
 * greeting as the first chat line, a quiet "For me · ♡ For someone" choice,
 * ONE gold "✦ Get Reading · {price} ✦" and "About {name} ›" beside it.
 * Everything else about the Mystic lives on the About page.
 *
 * The pure rules of it; ReaderPage draws it (MysticRoomDoor.vue).
 */

import { readerFacts } from '@/utils/reader-facts'

const COMMUNITY_SOURCES = new Set(['community', 'pulse', 'drop', 'just-arrived', 'drops'])
const PANELS = new Set(['community', 'altar', 'quest', 'studio'])

/**
 * Where the seeker came from, remembered on the way in.
 *  source  openReaderDetail's source ('community', 'pulse', 'drop', …)
 *  via     the event's via ('lesson', 'search', 'journal', 'seeker')
 *  lesson  the lesson's short id, for a lesson's Mystic mark
 *  panel   the panel the seeker was on when they tapped
 * null: an arrival by link, with nowhere in the app to go back to.
 */
export function roomOriginFrom ({ source = '', via = '', lesson = '', panel = '', mystic = '' } = {}) {
  if (via === 'lesson') return lesson ? { kind: 'lesson', lesson: String(lesson) } : null
  // A Mystic recommended in the chat (2026-10-30): back to the one the seeker was with.
  if (via === 'mystic') return mystic ? { kind: 'mystic', sub: String(mystic) } : null
  if (via === 'search') return { kind: 'search' }
  if (via === 'journal') return { kind: 'journal' }
  // The Lessons feed's invitation card (community-feed-invite §4.8): back to the Lessons.
  if (via === 'invite') return { kind: 'community' }
  if (via === 'seeker' || (!via && !source && panel)) {
    if (panel === 'community') return { kind: 'community' }
    return PANELS.has(panel) ? { kind: 'panel', panel } : null
  }
  if (COMMUNITY_SOURCES.has(source)) return { kind: 'community' }
  return null
}

/**
 * Where the room's ✕ goes, or null when it hides: an arrival by link, or a
 * newcomer whose home this Mystic is (no Community yet to go back to; the
 * navigation rule, ReaderPage's old `onReaderProfile && communityOpen`).
 */
export function roomCloseTarget ({ origin = null, communityOpen = false } = {}) {
  if (!origin) return null
  switch (origin.kind) {
    case 'community': return communityOpen ? { panel: 'community', tab: 'mystics' } : null
    case 'search': return communityOpen ? { panel: 'community', tab: 'seekers' } : null
    case 'lesson': return origin.lesson ? { lesson: origin.lesson } : null
    case 'journal': return { panel: 'altar', tab: 'history' }
    case 'mystic': return origin.sub ? { reader: origin.sub } : null
    case 'panel': return origin.panel === 'community' && !communityOpen ? null : { panel: origin.panel }
    default: return null
  }
}

/**
 * The one gold button. "For someone" turns its ✦ into ♡; the price is the
 * same either way (a reading is 100 gift or not; any gift calculation waits
 * for Send). An unknown price shows nothing rather than a guess.
 * The room (2026-10-30): no price when the first reading is free (never the
 * word "Free", Chris 2026-10-30); an unfinished reading is "Continue your reading", no price (already paid).
 */
export function roomCtaFace ({ forSomeone = false, price = '', free = false, continuing = false } = {}) {
  if (continuing) return { mark: forSomeone ? '♡' : '✦', text: 'Continue your reading', price: '' }
  return { mark: forSomeone ? '♡' : '✦', text: 'Get Reading', price: free ? '' : (price || '') }
}

/**
 * The Mystic's first chat line: the cached greeting of the day (one per
 * Mystic per day, server side), else the Mystic's own one line. Never a line
 * made up here.
 */
export function roomGreeting (data) {
  if (!data) return ''
  const g = data.greeting
  const text = (g && typeof g === 'object') ? g.text : g
  if (text && String(text).trim()) return String(text).trim()
  return data.teaser ? String(data.teaser).trim() : ''
}


// An origin noted by a surface that reaches the room through a ?reader= link
// (the Journal's "Visit {Mystic}"), taken once by the room for that Mystic.
let noted = null
export function noteRoomOrigin (sub, origin) {
  noted = sub && origin ? { sub: String(sub), origin } : null
}
export function takeNotedRoomOrigin (sub) {
  if (!noted || noted.sub !== String(sub || '')) return null
  const { origin } = noted
  noted = null
  return origin
}


// ── The room, the card that turns (2026-10-30, mystic-room-landing DESIGN) ──

const SYNODIC = 29.530588853
const NEW_MOON = Date.UTC(2000, 0, 6, 18, 14)
const PHASE_ENDS = [[1.84, 'New moon'], [5.53, 'Waxing crescent'], [9.22, 'First quarter'], [12.91, 'Waxing gibbous'],
  [16.61, 'Full moon'], [20.30, 'Waning gibbous'], [23.99, 'Last quarter'], [27.68, 'Waning crescent'], [99, 'New moon']]

/** Tonight's moon by the synodic month (the server's room_greeting does the same). */
export function moonOf (date = new Date()) {
  let age = ((date.getTime() - NEW_MOON) / 864e5) % SYNODIC
  if (age < 0) age += SYNODIC
  const name = PHASE_ENDS.find(([a]) => age < a)[1]
  const lit = (1 - Math.cos(2 * Math.PI * age / SYNODIC)) / 2
  return { age, name, lit, waxing: age < SYNODIC / 2 }
}

/**
 * The sky mark over the line: a drawn moon and "Waning gibbous · Friday 30
 * October". The server's phase (the one the line was written from) wins, so
 * the mark and the line agree.
 * The holiday line (2026-10-30): on a marked day (the server's `greeting.day`)
 * the day's name takes the phase's place; the moon stays drawn. The line never
 * wraps: `labels` are the tries in order (the weekday drops first, then the
 * short name), the door shows the first that fits. A solemn day comes with no
 * day, so it is the moon and the date.
 * Two marks on one day (2026-10-31, "We can have both"): the day's `also`
 * holds the others; the tries name them all first ("A & B", last the short
 * names alone, no date), then the main one as built; `names` lists them all.
 */
export function skyMark ({ phase = '', date = new Date(), day = null } = {}) {
  const moon = moonOf(date)
  const when = date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }).replace(',', '')
  const noWeekday = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })
  const card = day && typeof day === 'object' && day.name ? day : null
  const name = card ? card.name : (phase || moon.name)
  const labels = [`${name} · ${when}`, `${name} · ${noWeekday}`]
  if (card && card.short && card.short !== card.name) labels.push(`${card.short} · ${noWeekday}`)
  const all = card ? [card, ...(Array.isArray(card.also) ? card.also.filter((m) => m && m.name) : [])] : []
  if (all.length > 1) {
    const names = all.map((m) => m.name).join(' & ')
    const shorts = all.map((m) => m.short || m.name).join(' & ')
    const both = [`${names} · ${when}`, `${names} · ${noWeekday}`]
    if (shorts !== names) both.push(`${shorts} · ${noWeekday}`)
    // On a phone both names before the date: it is today.
    both.push(shorts)
    labels.unshift(...both)
    return { moon, label: labels[0], labels, day: card, names: all.map((m) => m.name) }
  }
  return { moon, label: labels[0], labels: card ? labels : [labels[0]], day: card }
}

/** The day's one glint across its name: the first visit of that day on this device. */
const GLINT_KEY = 'mysticRoomDayGlint'
export function dayGlintDue (key, isoDate, storage = (typeof window !== 'undefined' ? window.localStorage : null)) {
  try { return !!storage && storage.getItem(GLINT_KEY) !== `${key}:${isoDate}` } catch (e) { return false }
}
export function dayGlintSeen (key, isoDate, storage = (typeof window !== 'undefined' ? window.localStorage : null)) {
  try { if (storage) storage.setItem(GLINT_KEY, `${key}:${isoDate}`) } catch (e) { /* private mode */ }
}

/** The arrival plays on a fresh entry; back from About or a reading it is settled; the Quest passes through. */
export function roomScene ({ from = 'entry' } = {}) {
  if (from === 'quest-someone') return 'none'
  if (from === 'about' || from === 'reading' || from === 'played') return 'settled'
  return 'arrive'
}

/** The full bar and its 5px gap: the room keeps it free whatever the bar is, so every entry is identical. */
export const ROOM_RESERVE = 70
const CTA_H = 48
/** Room under Get Reading: none. No "A reading for someone" link there (Chris 2026-10-30). */
export const ROOM_SOMEONE_H = 0
const BACK_RATIO = 1.6

/**
 * One stage: the portrait, the sky mark and the line, and the button are one
 * group, centred a little high (44% of the free space above) between the top
 * band and the bottom reserve. W is the column's width, H its height, doorH
 * the sky mark and line's height. The back of the card is a taller tarot shape,
 * never taller than the space above the button. splitPct is where the top
 * pane ends (the portrait's foot), so Get Reading keeps the portrait still.
 */
export function roomLayout ({ W, H, doorH = 0, phone = false }) {
  const topBand = phone ? 56 : 64
  const pz = Math.round(Math.max(200, Math.min(H * (phone ? 0.36 : 0.34), W * 0.70, phone ? 300 : 340)))
  const group = pz + 22 + doorH + 26 + CTA_H + ROOM_SOMEONE_H
  const free = H - topBand - ROOM_RESERVE - group
  const top = topBand + Math.max(0, free * 0.44)
  const front = { w: pz, h: pz, x: (W - pz) / 2, y: top }
  const doorY = top + pz + 22
  const ctaY = doorY + doorH + 26
  const bw = Math.min(W - 32, phone ? 380 : (W < 1100 ? 440 : 400))
  const availB = H - topBand - ROOM_RESERVE - CTA_H - ROOM_SOMEONE_H - 22 - 10
  // The back is the whole About now (2026-10-30): a full tarot shape, 1.6
  // tall, so more of it shows before it scrolls (it was 1.34 / 1.24).
  const bh = Math.max(0, Math.min(availB, Math.round(bw * BACK_RATIO)))
  const by = topBand + Math.max(0, (availB - bh) * 0.5)
  const back = { w: bw, h: bh, x: (W - bw) / 2, y: by }
  // The most the sky mark and line may take with Get Reading still above the
  // bar (a long line, like Talk it through's parting line, scrolls in its box).
  const lineMax = H - topBand - ROOM_RESERVE - pz - 22 - 26 - CTA_H - ROOM_SOMEONE_H
  return { pz, topBand, front, back, doorY, ctaY, ctaYBack: by + bh + 22, splitPct: H > 0 ? (top + pz) / H * 100 : 0, lineMax }
}

/**
 * The line, word by word: one word every 80ms from 1.1s, +260ms after a full
 * stop, +120ms after a comma. Get Reading arrives 0.15s after the line ends,
 * never later than 3.0s.
 */
export function wordStream (text) {
  const parts = String(text || '').split(/\s+/).filter(Boolean)
  let t = 1100
  const words = parts.map((w) => {
    const at = t
    t += 80 + (/[.!?…]$/.test(w) ? 260 : /[,;:]$/.test(w) ? 120 : 0)
    return { text: w, at }
  })
  const lineEnd = t + 300
  return { words, lineEnd, ctaAt: Math.min(lineEnd + 150, 3000) }
}

const COUNT_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']
const plural = (n, one) => `${n} ${one}${n === 1 ? '' : 's'}`

/**
 * The back of the card, the whole About (2026-10-30, no second page): Reads
 * for, Voice, A reading (the kind, the spread's size and the price), So far
 * (readings, shared, followers), then the About page's Born and Created by
 * (its link kept). A row with nothing to say hides.
 */
export function backRows (data, { price = '' } = {}) {
  if (!data) return []
  const rows = []
  const say = (label, value) => { const v = String(value || '').trim(); if (v) rows.push({ label, value: v }) }
  say('Reads for', data.reads_for)
  say('Voice', data.voice_short)
  const skills = Array.isArray(data.skills) ? data.skills : []
  const kind = skills.includes('tarot') || !skills.length ? 'Tarot' : String(skills[0]).replace(/^\w/, (c) => c.toUpperCase())
  const n = data.active_spread && Number(data.active_spread.card_count)
  const size = n ? (n === 1 ? 'one card' : `${COUNT_WORDS[n] || n} cards`) : ''
  say('A reading', [kind, size, price].filter(Boolean).join(' · '))
  const st = data.stats || {}
  const reads = Number(st.reading_count) || 0
  const shared = Number(st.shared_count) || 0
  const followers = Number(data.followers_count) || 0
  say('So far', reads || followers
    ? [reads ? plural(reads, 'reading') : '', shared ? `${shared} shared` : '', followers ? plural(followers, 'follower') : ''].filter(Boolean).join(' · ')
    : '')
  for (const f of readerFacts(data)) {
    if (f.label === 'Readings') continue
    rows.push(f)
  }
  return rows
}

/**
 * The card's view for an entry that asked for a tab of the old About page:
 * 'deck' and 'shared' (Lessons) open in place, 'about' turns the card to its
 * summary, the default ('drop') leaves the card as it is.
 */
export function cardViewFor (tab) {
  if (tab === 'deck') return 'deck'
  if (tab === 'shared' || tab === 'lessons') return 'shared'
  if (tab === 'about' || tab === 'summary') return 'summary'
  return ''
}

/** The one-time turn cue, kept per device. */
const CUE_KEY = 'mysticRoomCardTurned'
export function cueDue (storage = (typeof window !== 'undefined' ? window.localStorage : null)) {
  try { return !storage || storage.getItem(CUE_KEY) !== '1' } catch (e) { return false }
}
export function cueSeen (storage = (typeof window !== 'undefined' ? window.localStorage : null)) {
  try { if (storage) storage.setItem(CUE_KEY, '1') } catch (e) { /* private mode */ }
}
